import {
  evaluateTemplateValue,
  dataLocationToPath,
} from "@ea-lab/reactive-json";

/**
 * Copies a resolved value to the system clipboard.
 *
 * Why a custom reaction (config-first escape hatch) : the clipboard is a
 * browser API, imperative by nature and asynchronous, with a permission model
 * and a documented fallback for non-secure contexts. reactive-json has no
 * primitive for it and could not have one — there is no store slot to write,
 * nothing to render, and the whole effect happens outside the data tree.
 *
 * It exists for exactly one screen requirement that has no alternative: an API
 * key secret is displayed ONCE, and asking someone to hand-transcribe 43
 * characters of base64 would guarantee transcription errors on a credential
 * that cannot be shown again.
 *
 * YAML usage :
 *   - { what: copyToClipboard, on: click, value: ~~.newSecret }
 *
 * Optional `statusPath` receives "copied" or "failed", so the page can react
 * declaratively (a `hide` on the confirmation) without this reaction owning any
 * markup. Pair it with the `notice` reaction for the screen-reader
 * announcement — a visual label change alone is not announced.
 *
 * Registered in [App.jsx](../../../App.jsx) under
 * `customPlugins.reaction.copyToClipboard`.
 */
export const copyToClipboardReaction = (props) => {
  const { args, globalDataContext, templateContext } = props;
  if (!args) return;

  const gdc = globalDataContext.getRootContext
    ? globalDataContext.getRootContext()
    : globalDataContext;

  const value = evaluateTemplateValue({
    valueToEvaluate: args.value,
    globalDataContext: gdc,
    templateContext,
  });

  if (value === null || value === undefined || value === "") return;
  const text = String(value);

  // `updateData` attend un CHEMIN résolu ("data.copyStatus"), pas un
  // dataLocation de la forme YAML : lui passer la forme brute écrit dans le
  // vide, sans la moindre erreur — l'écran reste muet, et un échec de copie
  // devient indiscernable d'un succès.
  const report = (status) => {
    if (typeof args.statusPath !== "string") return;
    const path = dataLocationToPath({
      dataLocation: args.statusPath,
      currentPath: templateContext?.templatePath,
      globalDataContext: gdc,
      templateContext,
    });
    if (path) gdc.updateData(status, path);
  };

  // navigator.clipboard is undefined outside a secure context (plain http on a
  // hostname other than localhost — which is exactly how this project is served
  // in development). Falling back keeps the button working there instead of
  // failing silently on the one screen where the value is unrecoverable.
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => report("copied"),
      () => report(legacyCopy(text) ? "copied" : "failed"),
    );
    return;
  }

  report(legacyCopy(text) ? "copied" : "failed");
};

/**
 * document.execCommand('copy') on a detached textarea.
 *
 * Deprecated, and the only thing that works without a secure context.
 */
function legacyCopy(text) {
  const previous = document.activeElement;
  const area = document.createElement("textarea");
  try {
    area.value = text;
    // Hors écran plutôt que masqué : un élément en display:none ou en
    // opacity:0 n'est pas sélectionnable, donc pas copiable.
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.top = "0";
    area.style.left = "-9999px";
    document.body.appendChild(area);
    area.focus();
    area.select();
    // iOS et quelques navigateurs ignorent select() sur un champ readonly :
    // setSelectionRange est le seul appel qui étend vraiment la sélection.
    area.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    area.remove();
    // Rendre le focus : la modale a un piège à focus, et le lui voler sans le
    // rendre laisse la touche Échap sans effet.
    if (previous instanceof HTMLElement) previous.focus();
  }
}
